import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { Button, HelpIcon, Icon, Menu, PUBLIC_REPLY_HELP, type MenuSection } from "@omnabase/ui";
import type { ToChoice } from "@omnabase/shared";
import type { CopyRow, ToLineHandle } from "./useToLine";

/** The To line (the To line round, 2026-10-30; brief rule 1, "above the box is who, below the box is how"): one collapsed line at the top
 *  of the reply composer and of the AI draft's card, "To ada@kunde.test · Cc it@kunde.test" or "To Ada in the forum topic". The whole line
 *  is a button; it opens in place into three rows: To (one chip with a menu since the write_draft round; another address keeps the warning tone), Cc and Bcc (address
 *  chips, a field that takes an address or a pasted list on Enter). Escape or Done closes it. On a public forum topic the line carries the
 *  public tone and the help icon that says what the site's mode means (it moved here from the route line under the box). */
export function ToLine({ line, publicReplies }: { line: ToLineHandle; /** how the ticket's site answers public topics */ publicReplies?: "draft" | "auto" }) {
  const tone = `ob-toline${line.isPublic ? " ob-toline--public" : ""}${line.warning ? " ob-toline--warning" : ""}`;
  const help = line.isPublic ? <HelpIcon label="What a public reply means" text={`Everyone on the forum reads it. ${PUBLIC_REPLY_HELP[publicReplies ?? "draft"]}`} /> : null;
  if (!line.open) {
    return (
      <div className={tone}>
        <button type="button" className="ob-toline__summary" aria-label={`Change who gets this reply: ${line.words}`} onClick={() => line.setOpen(true)}>{line.words}</button>
        {line.warning && <span className="ob-toline__warning">{line.warning}</span>}
        {help}
      </div>
    );
  }
  return (
    // Escape closes the line only from inside it: the To menu lives in a portal (its keys still bubble here through React) and closes itself (finding 15)
    <div className={`${tone} ob-toline--open`} role="group" aria-label="Who gets this reply" onKeyDown={(e) => { if (e.key === "Escape" && e.currentTarget.contains(e.target as Node)) { e.stopPropagation(); line.setOpen(false); } }}>
      <ToRow line={line} />
      <CopiesRow line={line} row="cc" label="Cc" />
      <CopiesRow line={line} row="bcc" label="Bcc" />
      {line.error && <div className="ob-toline__error" role="alert">{line.error}</div>}
      <div className="ob-toline__foot">{line.warning && <span className="ob-toline__warning">{line.warning}</span>}{help}<Button size="sm" onClick={() => line.setOpen(false)}>Done</Button></div>
    </div>
  );
}

/** The To row (the write_draft round, 2026-10-31; brief rule 1 and findings 1, 14, 15): one chip with the one destination, as a mail program's
 *  To shows only what applies; a click opens the kit's menu with the ticket's choices (the current one ticked) and "Another address…", which
 *  puts the address field in the chip's place with the focus in it. Escape or leaving the empty field brings the chip back. */
function ToRow({ line }: { line: ToLineHandle }) {
  const [typing, setTyping] = useState(false);
  const otherPicked = useRef(false); const chipBack = useRef(false); const chip = useRef<HTMLButtonElement>(null);
  useEffect(() => { if (!typing && chipBack.current) { chipBack.current = false; chip.current?.focus(); } }, [typing]);
  const done = () => { chipBack.current = true; setTyping(false); };
  const label = toRowLabel(line);
  return (
    <div className="ob-toline__row">
      <span className="ob-toline__label">To</span>
      <div className="ob-toline__field">
        {typing
          ? <AddressInput label="Another address" placeholder="Another address" focus onCancel={done} onCommit={(text) => { const ok = line.typeTo(text); if (ok) done(); return ok; }} />
          : <Menu align="start" sections={toRowSections(line, () => { otherPicked.current = true; setTyping(true); })}
              onCloseAutoFocus={(e) => { if (otherPicked.current) { otherPicked.current = false; e.preventDefault(); } }}
              trigger={<button ref={chip} type="button" className="ob-toline__choice ob-toline__choice--on" aria-label={`To: ${label}. Change where this reply goes`}>{label}<Icon name="chevronDown" size="sm" className="ob-toline__chevron" /></button>} />}
      </div>
    </div>
  );
}

/** a choice is the current one when its way and (by mail) its address are the line's */
const isPicked = (line: ToLineHandle, c: ToChoice) => c.via === line.line.via && (c.via === "site" || c.to === line.line.to);
/** the chip's words: the picked choice's label, else the typed address (a To that is none of the ticket's choices) */
function toRowLabel(line: ToLineHandle): string {
  const picked = line.choices.find((c) => isPicked(line, c));
  if (picked) return picked.label;
  return line.line.via === "mail" && line.line.to ? line.line.to : line.words;
}
/** the To menu's entries (finding 14: pure, so a test reads them without opening a menu): the ticket's choices in their order with the current
 *  one ticked, then "Another address…" */
export function toRowSections(line: ToLineHandle, onOther: () => void): MenuSection[] {
  return [
    { items: line.choices.map((c) => ({ id: `${c.via}:${c.to ?? ""}`, label: c.label, checked: isPicked(line, c), onSelect: () => line.pick(c) })) },
    { items: [{ id: "another-address", label: "Another address…", icon: "pen", onSelect: onOther }] },
  ];
}

/** a Cc or Bcc row: the chips (each removable) and the field */
function CopiesRow({ line, row, label }: { line: ToLineHandle; row: CopyRow; label: string }) {
  return (
    <div className="ob-toline__row">
      <span className="ob-toline__label">{label}</span>
      <div className="ob-toline__field">
        {line.line[row].map((a) => (
          <span key={a} className="ob-toline__chip">{a}<button type="button" className="ob-toline__chip-remove" aria-label={`Remove ${a}`} onClick={() => line.remove(row, a)}><Icon name="x" size="sm" /></button></span>
        ))}
        <AddressInput label={label} placeholder="Add an address" focus={row === "cc"} onCommit={(text) => line.add(row, text)} />
      </div>
    </div>
  );
}

/** a field that takes an address (or a pasted list) on Enter or on leaving it; refused text stays for correcting. `focus`: takes the focus when
 *  it appears (again a moment later, after a closing menu or sheet has put the focus back on its trigger); `onCancel`: Escape, or leaving it
 *  empty (the To row's field gives the chip back) */
function AddressInput({ label, placeholder, onCommit, onCancel, focus }: { label: string; placeholder: string; onCommit: (text: string) => boolean; onCancel?: () => void; focus?: boolean }) {
  const [value, setValue] = useState("");
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!focus) return;
    ref.current?.focus();
    const later = setTimeout(() => ref.current?.focus(), 0);
    return () => clearTimeout(later);
  }, [focus]);
  const commit = () => { if (value.trim() && onCommit(value)) setValue(""); };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape" && onCancel) { e.preventDefault(); e.stopPropagation(); onCancel(); return; }
    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); e.stopPropagation(); commit(); }
  };
  const onBlur = () => { if (!value.trim()) onCancel?.(); else commit(); };
  return <input ref={ref} className="ob-toline__input" type="text" inputMode="email" autoComplete="off" aria-label={label} placeholder={placeholder} value={value} onChange={(e) => setValue(e.target.value)} onKeyDown={onKey} onBlur={onBlur} />;
}
